import { signal, Signal } from '@preact/signals';
import { CATEGORY_ALL_CATEGORIES, type CategoryData, getCategoryPath } from 'service/category/model';
import { getSuggests } from 'service/search/resource';
import { type SuggestionData } from 'service/search/model';
import { filters } from 'signals/filters';
import { queryOffers } from 'signals/queryOffers';
import { phraseCategories } from 'components/filters/categoryList/signals.ts';
import { SessionStorage } from 'utils/SessionStorage.ts';
import { SearchType } from 'lib/dot/enum/searchType.ts';
import { sendSearchAnalytics } from 'lib/dot/search/utils.ts';
import { LocalStorageKeys, LocalStorageSignal } from 'utils/LocalStorage';
import isArray from 'lodash/isArray';
import { getAnalyticsCategoryAmount } from 'service/category/utils.ts';
import { availableCategories } from 'components/filters/categoryList/signals.ts';
import { navigate } from 'astro:transitions/client';
import { activeCategory, categoriesData, displayCategories } from 'signals/category';
import { ApiError } from 'utils/apiError';

const CATEGORIES_LIMIT = 2;
const MAX_SAVED_SEARCHES = 3;
const SAVED_SEARCH_DELIMITER = '|';

export const HP_SEARCH_FIELD_ID = 'hp-search-field';
export const HP_SEARCH_BLOCK_ID = 'hp-search-block';
export const HP_SEARCH_LOCATION_ID = 'hp-search-location';
export const SEARCH_BLOCK_ID = 'search-block';
export const SEARCH_FIELD_ID = 'search-field';
export const SEARCH_LOCATION_ID = 'search-location';

export const isScrolled = signal(false);
export const searchCategorySeoName = signal<CategoryData | null>(null);
// On HP, selecting a suggestion for query input does not immediately redirect to the search page. Use this variable to
// store whether the user typed some text himself or selected a suggestion.
export const searchQueryFromSuggest = signal<boolean>(false);
export const suggestsData = signal<SuggestionData[]>([]);
export const suggestsDataLocal = signal<boolean>(false); // Latest searches in LocalStorage

globalThis?.document?.addEventListener('astro:after-swap', () => {
	searchCategorySeoName.value = CATEGORY_ALL_CATEGORIES;
	searchQueryFromSuggest.value = false;
});

let latestSuggestRequestId = 0;
let latestSuggestRequestQuery = ' '; // Prevents repeated requests when focusing/blurring the search input. Initial value is space for initialization of saved search

const savedSearch = new LocalStorageSignal<SuggestionData[]>(LocalStorageKeys.LAST_SEARCH_QUERIES, {
	defaultValue: [] as SuggestionData[],
	transformToData: (str) =>
		str?.split('|').map((sentence, index) => ({
			sentence,
			categories: [],
			highlight: [],
			savedSearchId: index,
		})) ?? [],
	transformToString: (value) =>
		value
			.map(({ sentence }) => sentence)
			.slice(0, MAX_SAVED_SEARCHES)
			.join(SAVED_SEARCH_DELIMITER),
});

export function clearSuggestions() {
	latestSuggestRequestId++;
	latestSuggestRequestQuery = '';
	suggestsData.value = [];
}

export async function refreshSuggestions(query: string, showSuggests: Signal<boolean>) {
	if (latestSuggestRequestQuery === query) return;

	latestSuggestRequestQuery = query;
	showSuggests.value = true;
	if (query.length === 0) {
		++latestSuggestRequestId;
		suggestsData.value = savedSearch.value;
		suggestsDataLocal.value = true;
	} else {
		const requestId = ++latestSuggestRequestId;
		const data = await getSuggests({ count: '7', phrase: query }).catch((error) => {
			if (error instanceof ApiError && error.cause.status === 404) {
				return [];
			}

			console.error('Failed to fetch search suggestions', error);
			return [];
		});
		// Only update the suggestions if the request is the latest one, i.e. still relevant
		if (latestSuggestRequestId === requestId) {
			suggestsData.value = mapCategoriesAndSuggests(data, filters.value.category?.id);
			suggestsDataLocal.value = false;
		}
	}
}

export function storeLatestSearch(query: string) {
	const trimmedQuery = query.trim();
	if (!trimmedQuery) return;

	const lastSearch = new Set(savedSearch.value.map((s) => s.sentence));
	if (lastSearch.has(trimmedQuery)) return;

	savedSearch.value = [
		{
			sentence: trimmedQuery,
			categories: [],
			highlight: [],
			savedSearchId: Math.max(0, ...savedSearch.value.map((s) => s.savedSearchId || 0)) + 1,
		},
		...savedSearch.value.slice(0, MAX_SAVED_SEARCHES - 1),
	];
}

export function removeSavedSearchId(id: number) {
	suggestsData.value = savedSearch.value = savedSearch.value.filter((search) => search.savedSearchId !== id);
}

export function handleClickOutsideOfSearchBlock(
	event: MouseEvent,
	searchBlockId: string,
	searchLocationId: string,
	showSuggests: Signal<boolean>,
) {
	const searchBlock = globalThis.document.getElementById(searchBlockId);
	const locationInput = globalThis.document.getElementById(searchLocationId);

	if (!searchBlock) {
		return;
	}

	// Clicking outside the search box OR clicking on the other input inside the block should disable the suggestions
	if (
		!searchBlock.contains(event.target as Node) ||
		(locationInput && locationInput.contains(event.target as Node))
	) {
		showSuggests.value = false;
	}
}

function makeCategorySuggest(sentence: any, highlight: any, categoryId: number, categoryList: CategoryData[]) {
	const categoryEntityPath =
		categoryId === 0
			? [CATEGORY_ALL_CATEGORIES]
			: categoryId
				? getCategoryPath(categoryId, categoryList)
				: categoryList;

	let categorySeoUrl = null;

	if (categoryId) {
		categorySeoUrl = categoryList[categoryList.length - 1]?.seoName;
	}

	return {
		sentence,
		highlight,
		categoryPath: categoryEntityPath,
		categories: [],
		url: categorySeoUrl,
	};
}

function mapCategoriesAndSuggests(suggestsData: SuggestionData[], searchCategoryId?: number | null): SuggestionData[] {
	const firstSuggest = suggestsData[0];

	if (!firstSuggest) {
		return [];
	}

	const { sentence, categories, highlight } = firstSuggest;
	const searchInAll = makeCategorySuggest(sentence, highlight, 0, []);

	const permanentCategories = [];

	if (searchCategoryId) {
		const searchCategory = makeCategorySuggest(sentence, highlight, searchCategoryId, categoriesData.value);
		permanentCategories.push(searchCategory);
	}

	permanentCategories.push(searchInAll);

	const categorySuggests = categories
		.slice(0, CATEGORIES_LIMIT)
		.filter((categoryId: number) => !searchCategoryId || categoryId !== searchCategoryId)
		.map((categoryId: number) => makeCategorySuggest(sentence, highlight, categoryId, categoriesData.value));

	const usedSuggests = suggestsData.slice(1);

	return [...permanentCategories, ...categorySuggests, ...usedSuggests];
}

function checkIfShouldRedirect() {
	return (
		Boolean(globalThis?.document?.getElementById('errorStatus')) ||
		globalThis?.location?.pathname.match(/^\/((hledej)|(\d+(-\w+))).*/) === null
	);
}

export async function handleSubmit(
	event: Event,
	isSearchBoxFocused: Signal<boolean>,
	showSuggests: Signal<boolean>,
	isFromSuggestion: boolean,
) {
	event.preventDefault();

	const filtersValue = filters.peek();
	if (filtersValue.hledej.seoName) {
		storeLatestSearch(filtersValue.hledej.seoName);
	}

	const newCategory = searchCategorySeoName.value || filters.value.category;

	// Clicking the suggest and submitting the form may have changed some filters, so we need to refresh suggestions, also we get them eagerly
	clearSuggestions();
	refreshSuggestions(filters.value.hledej.seoName ?? '', showSuggests);
	isSearchBoxFocused.value = false;

	if (!newCategory || newCategory.id === CATEGORY_ALL_CATEGORIES.id) {
		activeCategory.value = null;
	}

	filters.value = filters.getUpdatedValue({
		category: newCategory,
		hledej: { seoName: filters.value.hledej.seoName },
	});

	showSuggests.value = false;

	const searchType = isFromSuggestion ? SearchType.SUGGEST : SearchType.STRING;
	if (checkIfShouldRedirect()) {
		SessionStorage.setSearchMeta({ searchType });
		navigate(filters.generateUrl({}));
	} else {
		const [offers, categories] = await queryOffers();
		const filtersValue = filters.peek();
		sendSearchAnalytics({
			searchType,
			useCurrentPageType: true,
			filters: filtersValue,
			categoryCount:
				((filtersValue.shop || filtersValue.isAdmin || filtersValue.seller) &&
					getAnalyticsCategoryAmount(availableCategories.peek(), activeCategory.peek()?.id)) ??
				((isArray(categories) && categories?.length) || phraseCategories.peek()?.length) ??
				displayCategories.peek().length ??
				null,
			itemCount: offers.pagination.total,
			resolvedLocality: filters.peek().resolvedLocality,
		});
	}
}
